<template>
  <el-card>
    <div slot="header" class="clearfix">
      <h3 style="line-height: 36px">
        Management Dashboard - Audit Log
        <!-- <small>{{ noOfItems }} logs shown</small> -->
      </h3>
      <div style="float: right">
        <el-input
          size="mini"
          v-model="search"
          placeholder="Search"
          prefix-icon="el-icon-search"
          clearable
        />
      </div>
    </div>
    <el-row type="flex" align="middle">
      <small style="font-weight: bold">Filter by date range</small>
      <el-date-picker
        v-model="searchDateRange"
        type="daterange"
        format="yyyy-MM-dd"
        style="width: 400px; margin-left: 15px; margin-bottom: 0"
        align="right"
        unlink-panels
        range-separator="To"
        start-placeholder="Start date"
        size="mini"
        end-placeholder="End date"
        :picker-options="pickerOptions"
        @change="handleDateRangeChange"
      ></el-date-picker>
      <el-select
        v-model="selectedEvent"
        clearable
        filterable
        placeholder="Event"
        size="mini"
        style="width: 240px; margin-left: 15px"
        @change="handleFilterChange"
      >
        <el-option-group v-for="group in eventGroups" :key="group.category" :label="group.category">
          <el-option
            v-for="ev in group.events"
            :key="ev"
            :label="eventOptionLabel(ev)"
            :value="ev"
          />
        </el-option-group>
      </el-select>
      <el-input
        v-model="performedBy"
        clearable
        placeholder="Performed by (email)"
        size="mini"
        style="width: 200px; margin-left: 15px"
        @change="handleFilterChange"
      />
      <el-select
        v-model="entityType"
        clearable
        placeholder="Item type"
        size="mini"
        style="width: 150px; margin-left: 15px"
        @change="handleFilterChange"
      >
        <el-option label="Content node" value="content" />
        <el-option label="Trigger" value="trigger" />
        <el-option label="Entity" value="entity" />
        <el-option label="API config" value="api_config" />
        <el-option label="API mapping" value="api_mapping" />
        <el-option label="Setting" value="setting" />
      </el-select>
      <el-input
        v-model="entityId"
        clearable
        placeholder="Item name / ID"
        size="mini"
        style="width: 180px; margin-left: 15px"
        @change="handleFilterChange"
      />
      <el-checkbox v-model="enableRealtime" style="margin-left: 20px"
        >Enable Realtime Updates</el-checkbox
      >
    </el-row>
    <br />
    <div class="block">
      <el-pagination
        background
        small
        layout="prev, pager, next"
        :pageSize="pageSize"
        :current-page.sync="currentPage"
        :total="noOfItems"
        @prev-click="togglePage"
        @next-click="togglePage"
        @current-change="togglePage"
        @size-change="handleSizeChange"
        style="display: inline-block"
      ></el-pagination>
      <div style="display: inline-block">
        <span>{{ noOfItems }} records</span>
      </div>
    </div>
    <div>
      <el-table
        size="mini"
        v-loading="loading"
        element-loading-text="Loading audit logs.."
        :data="pagedAuditLogs"
        style="width: 100%"
      >
        <el-table-column type="expand">
          <template slot-scope="props">
            <table class="flatten">
              <tr>
                <th>User Agent</th>
                <td>{{ props.row.user_agent }}</td>
              </tr>
              <tr>
                <th>User ID</th>
                <td>{{ props.row.user_id }}</td>
              </tr>
              <template v-if="!isFlatAble(props.row.data)">
                <tr>
                  <th>data</th>
                  <td>{{ props.row.data }}</td>
                </tr>
              </template>
              <template v-else>
                <tr :key="`${props.row.id}-${key}`" v-for="(item, key) in flatten(props.row.data)">
                  <th>{{ key }}</th>
                  <td>{{ formatAuditDataItem(key, item) }}</td>
                </tr>
              </template>
            </table>
          </template>
        </el-table-column>
        <el-table-column label="Time" min-width="150">
          <template slot-scope="scope">
            <strong>{{ scope.row.Timestamp | fromNow }}</strong>
            <br />
            <small>{{ scope.row.Timestamp | moment }}</small>
          </template>
        </el-table-column>
        <el-table-column label="Email" min-width="180">
          <template slot-scope="scope">{{ scope.row.user_email }}</template>
        </el-table-column>
        <el-table-column label="Actions taken" min-width="220">
          <template slot-scope="scope">
            <div>{{ eventLabel(scope.row.event) }}</div>
            <small class="event-raw">{{ scope.row.event }}</small>
          </template>
        </el-table-column>
        <el-table-column label="Changed item" min-width="200">
          <template slot-scope="scope">
            <template v-if="scope.row.entity_type">
              <el-tag size="mini" :type="entityTagType(scope.row.entity_type)" disable-transitions>
                {{ entityTypeLabel(scope.row.entity_type) }}
              </el-tag>
              <span class="entity-id">{{ scope.row.entity_id }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column label="Origin" min-width="180">
          <template slot-scope="scope">{{ scope.row.origin }}</template>
        </el-table-column>
        <el-table-column label="IP Address" min-width="120">
          <template slot-scope="scope">{{ scope.row.ip_address }}</template>
        </el-table-column>
      </el-table>
    </div>
  </el-card>
</template>
<script>
import moment from "moment";
import { gql } from "@apollo/client/core";
import _ from "lodash";
import { defaultPickerOptions, updateUrlQuery } from "@/helperMethods/util";
import { flatten, isFlatAble } from "@/helpers/flatten";

// Friendly, human-readable label for the "Changed item" type.
const ENTITY_TYPE_LABELS = {
  content: "Content node",
  trigger: "Trigger",
  entity: "Entity",
  api_config: "API config",
  api_mapping: "API mapping",
  setting: "Setting",
};

// el-tag colour per entity type (Element UI tag types).
const ENTITY_TAG_TYPES = {
  content: "",
  trigger: "success",
  entity: "warning",
  api_config: "info",
  api_mapping: "info",
  setting: "",
};

// Friendly labels for common audit events. Unmapped events fall back to a
// prettified version of the raw code (see eventLabel), so new events never break.
// Action part only (the ">" category prefix is added by eventLabel/eventCategory).
const EVENT_ACTION_LABELS = {
  "BOT:CONTENT:CREATED": "Content node created",
  "BOT:CONTENT:MODIFIED": "Content node updated",
  "BOT:CONTENT:DELETED": "Content node deleted",
  "BOT:TRIGGER:CREATED": "Trigger created",
  "BOT:TRIGGER:MODIFIED": "Trigger updated",
  "BOT:TRIGGER:DELETED": "Trigger deleted",
  "BOT:ENTITY:CREATED": "Entity created",
  "BOT:ENTITY:MODIFIED": "Entity updated",
  "BOT:ENTITY:DELETED": "Entity deleted",
  "BOT:MODULES:SET": "Updated",
  "BOT:MODULES:MODIFIED": "Updated",
  "BOT:GREETING:MODIFIED": "Greeting updated",
  "BOT:MENU:MODIFIED": "Persistent menu updated",
  "BOT:API_SETTINGS:CREATE_API": "API created",
  "BOT:API_SETTINGS:SAVE_API": "API updated",
  "BOT:API_SETTINGS:DELETE_API": "API deleted",
  "BOT:API_SETTINGS:CREATE_API_MAPPING": "API mapping created",
  "BOT:API_SETTINGS:SAVE_API_MAPPING": "API mapping updated",
  "BOT:API_SETTINGS:DELETE_API_MAPPING": "API mapping deleted",
  "USER:LOGOUT": "Logged out",
};

// Group events into user-facing categories by their code prefix.
function eventCategory(ev) {
  if (/^BOT:(CONTENT|TRIGGER):/.test(ev)) return "Nodes";
  if (/^BOT:ENTITY:/.test(ev)) return "Entities";
  if (/^BOT:(MODULES|GREETING|MENU)|EXPIRY/.test(ev)) return "Settings";
  if (/^BOT:API_SETTINGS:/.test(ev)) return "Integrations";
  if (/^BOT:LIVE_CHAT:/.test(ev)) return "Live Chat";
  if (/^(BOT:FAQW|FAQ)/.test(ev)) return "FAQ";
  if (/^BOT:FLOW_MAKER:/.test(ev)) return "Flows";
  if (/^BOT:MAKER_CHECKER:/.test(ev)) return "Publishing";
  if (/^GENAI/.test(ev)) return "GenAI";
  if (/^(USER|AGENTS|ACCESS_CONTROL)/.test(ev)) return "System";
  return "Other";
}

export default {
  data() {
    const startDate = _.get(this.$route, "query.startDate");
    const endDate = _.get(this.$route, "query.endDate");
    return {
      search: _.get(this.$route, "query.search") || "",
      selectedEvent: null,
      performedBy: "",
      entityType: null,
      entityId: "",
      auditEventTypes: [],
      loading: false,
      pagedAuditLogs: [],
      totalRecordCount: 0,
      searchDateRange:
        startDate && endDate
          ? [moment(startDate).toDate(), moment(endDate).toDate()]
          : [moment().toDate(), moment().toDate()],
      pageSize: 50,
      currentPage: 1,
      enableRealtime: true,
      pickerOptions: defaultPickerOptions,
    };
  },
  created() {
    this.$store
      .dispatch("FETCH_AUDIT_EVENT_TYPES")
      .then((list) => {
        this.auditEventTypes = list || [];
      })
      .catch(() => {});
  },
  computed: {
    noOfItems() {
      return this.totalRecordCount;
    },
    // Group the distinct events into user-facing categories for the dropdown.
    eventGroups() {
      const groups = {};
      (this.auditEventTypes || []).forEach((ev) => {
        const cat = eventCategory(ev);
        (groups[cat] = groups[cat] || []).push(ev);
      });
      return Object.keys(groups)
        .sort()
        .map((category) => ({ category, events: groups[category] }));
    },
  },
  filters: {
    moment: function (date) {
      return moment(date).local().format("D MMM YY h:mm:ss A");
    },
  },
  watch: {
    // whenever question changes, this function will run
    search: _.debounce(function (newSearch, oldSearch) {
      this.currentPage = 1;
      this.getAuditLogs();
    }, 500),
    enableRealtime(newVal, oldVal) {
      if (newVal === true) {
        this.getAuditLogs();
        this.$apollo.subscriptions["auditLogChanged"].start();
      } else {
        this.$apollo.subscriptions["auditLogChanged"].stop();
      }
    },
  },
  methods: {
    flatten,
    isFlatAble,
    // Human-readable event name; falls back to a prettified raw code.
    // Title-case a raw segment: "AGENT_CONNECT" -> "Agent Connect".
    prettifySegment(seg) {
      return seg
        .toLowerCase()
        .split("_")
        .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
        .join(" ");
    },
    // The action part (no category); mapped, else the raw segments after the domain.
    eventAction(ev) {
      if (EVENT_ACTION_LABELS[ev]) return EVENT_ACTION_LABELS[ev];
      const parts = ev.replace(/^BOT:/, "").split(":");
      const rest = parts.length > 1 ? parts.slice(1) : parts;
      return rest.map((seg) => this.prettifySegment(seg)).join(" › ");
    },
    // Full hierarchy label for the table: "Category › Action" (like Live Chat › X).
    eventLabel(ev) {
      if (!ev) return ev;
      return `${eventCategory(ev)} › ${this.eventAction(ev)}`;
    },
    // Dropdown option text: "Friendly Name (RAW:CODE)".
    eventOptionLabel(ev) {
      return `${this.eventAction(ev)} (${ev})`;
    },
    entityTypeLabel(type) {
      return ENTITY_TYPE_LABELS[type] || type;
    },
    entityTagType(type) {
      return ENTITY_TAG_TYPES[type] || "info";
    },
    formatAuditDataItem(key, item) {
      const auditDataTimeFieldPattern =
        /timestamp|date|time|created|modified|updated|resolve|queue/i;

      if (!auditDataTimeFieldPattern.test(String(key))) {
        return item;
      }

      const timestamp = moment(item);
      return timestamp.isValid() ? this.$options.filters.moment(item) : item;
    },
    parseJSON(data) {
      try {
        const result = JSON.parse(data);
        if (result && Object.keys(result).length === 0) {
          return { meta: "No additional information" };
        }
        return result;
      } catch (error) {
        console.error("audit parseJSON failed");
        return data;
      }
    },
    togglePage(pageNo) {
      this.currentPage = pageNo;
      this.getAuditLogs();
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.getAuditLogs();
    },
    handleDateRangeChange() {
      this.currentPage = 1;
      this.getAuditLogs();
    },
    handleFilterChange() {
      this.currentPage = 1;
      this.getAuditLogs();
    },
    getAuditLogs() {
      this.loading = true;
      let startDate = "";
      let endDate = "";
      if (this.searchDateRange && this.searchDateRange.length > 0) {
        startDate = this.searchDateRange[0];
        endDate = this.searchDateRange[1];
      }
      updateUrlQuery({
        search: this.search,
        startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : null,
        endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : null,
      });
      this.$store
        .dispatch("FETCH_AUDIT_LOG_BY_PAGE", {
          page: this.currentPage,
          pageSize: this.pageSize,
          searchString: this.search,
          startDate: startDate,
          endDate: endDate,
          event: this.selectedEvent || null,
          performedBy: this.performedBy || null,
          entityType: this.entityType || null,
          entityId: this.entityId || null,
        })
        .then((response) => {
          this.loading = false;
          this.pagedAuditLogs = _.get(response, "data.Bot.audit.rows", []);
          this.totalRecordCount = _.get(response, "data.Bot.audit.count", 0);
        })
        .catch((err) => {
          this.loading = false;
          this.$notify.error({
            title: "Error",
            position: "bottom-right",
            message: `Error fetching audit logs.`,
          });
        });
    },
  },
  apollo: {
    $subscribe: {
      auditLogChanged: {
        query: gql`
          subscription {
            auditLogChanged
          }
        `,
        // Result hook
        result(data) {
          const changedAuditLog = _.get(data, "data.auditLogChanged");

          if (changedAuditLog) {
            this.pagedAuditLogs = [data.data.auditLogChanged].concat(this.pagedAuditLogs);
            this.totalRecordCount = this.totalRecordCount + 1;
          }
        },
      },
    },
  },
  mounted() {
    this.getAuditLogs();
  },
};
</script>

<style scoped lang="scss">
@import "../assets/scss/colors.scss";

table.flatten {
  font-family: arial, sans-serif;
  border-collapse: collapse;
  width: 100%;
}

table.flatten td,
table.flatten th {
  border: 1px solid $color-dark;
  text-align: left;
  padding: 4px 10px;
  background-color: transparent;
}

table.flatten tr:nth-child(even) {
  background-color: $color-light;
}

.event-raw {
  color: #909399;
  font-size: 11px;
}

.entity-id {
  margin-left: 6px;
}
</style>
